import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { formatOfficerDateTime } from '../../lib/officerFormat'
import { isReconTaskType } from '../../lib/tasks'
import { useNavigationParams } from '../navigation/history'
import {
  recordQuery,
  workbenchHref,
  type RecordContext,
  type ViewScope,
} from '../navigation/context'
import { useRead } from '../navigation/useRead'
import { ErrorState, Skeleton } from '../state'
import FundSelector from '../FundSelector'
import WorkbenchHeader from './WorkbenchHeader'
import ContextRecovery from './ContextRecovery'
import ContextTaskPane from './ContextTaskPane'
import { parseRecoveryItem, recoveryQuery } from './recovery'
import { scopeLabel } from './contextWork'
import { otherHref, otherRecoveryHref, otherType, type OtherItem } from './otherWork'
import './other-workbench.css'

function OtherTaskReview({
  item,
  scope,
  onResolved,
}: {
  item: OtherItem
  scope: ViewScope
  onResolved: () => void
}) {
  const read = useRead<unknown>(
    item.task_id ? recoveryQuery(scope, { taskId: item.task_id }) : null
  )
  if (read.loading) return <Skeleton kind="cards" n={1} height={180} />
  let failure = read.error
  try {
    if (!failure) {
      const admitted = parseRecoveryItem(read.value, scope, item.task_id!)
      if (
        admitted.task_type !== item.task_type ||
        (item.target && admitted.subject && admitted.subject.id !== item.target.id)
      )
        failure = new Error('This work changed. Refresh the list before continuing.')
    }
  } catch (error) {
    failure = error
  }
  if (failure) return <ErrorState what="selected Other item" error={failure} onRetry={onResolved} />
  // Reuse existing officer panes only. Unclassified/admin tasks retain their
  // admitted recovery view; this list never publishes generic admin verbs.
  if (
    item.task_id &&
    item.task_type &&
    (isReconTaskType(item.task_type) || item.task_type === 'look_through_review')
  )
    return <ContextTaskPane taskId={item.task_id} onResolved={onResolved} />
  return (
    <p>
      <Link to={otherRecoveryHref(scope, item)}>Open existing review ›</Link>
    </p>
  )
}

export default function OtherWorkbench({ scope }: { scope: ViewScope }) {
  const [params, setParams] = useNavigationParams()
  const navigate = useNavigate()
  const [refresh, setRefresh] = useState(0)
  const read = useRead<RecordContext>(recordQuery(scope, { proposed: true }), refresh)
  const other = read.value?.other_work
  const work = read.value?.work ?? null
  const type = params.get('other_type') ?? ''
  const oldest = params.get('other_sort') === 'updated'
  const selected = other?.items.find((item) => item.id === params.get('item'))
  const types = [...new Set(other?.items.map(otherType) ?? [])].sort()
  const rows = (other?.items ?? [])
    .filter((item) => !type || otherType(item) === type)
    .slice()
    .sort((a, b) => {
      if (!oldest) {
        const rank = (p: string | null) =>
          p === 'urgent' ? 0 : p === 'high' ? 1 : p === 'normal' ? 2 : 3
        const by = rank(a.priority) - rank(b.priority)
        if (by) return by
      }
      return (b.updated_at ?? '').localeCompare(a.updated_at ?? '') || a.id.localeCompare(b.id)
    })
  function filter(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete('item')
    setParams(next)
  }
  const reload = () => setRefresh((n) => n + 1)
  return (
    <main className="context-workbench other-workbench" data-testid="other-workbench">
      <WorkbenchHeader
        scopeLabel={read.value ? scopeLabel(scope, read.value) : 'Selected context'}
        work={work}
        other={other}
        selected="other"
        onSelect={(queue) => navigate(workbenchHref(scope, queue))}
      />
      <div className="context-workbench__body">
        {read.loading ? <Skeleton kind="cards" n={1} height={200} /> : null}
        {read.error ? <ErrorState what="Other work" error={read.error} onRetry={reload} /> : null}
        {!read.loading && !read.error ? (
          <>
            <div className="other-controls">
              <label>
                Fund
                <FundSelector
                  value={scope.fund_id ?? null}
                  funds={
                    other
                      ? (work?.funds ?? []).map((f) => ({
                          id: f.id,
                          name: f.name,
                          open: other.items.filter((item) => item.fund_ids.includes(f.id)).length,
                        }))
                      : []
                  }
                  noun={other?.complete ? 'identified' : 'loaded'}
                  loading={!other}
                  onChoose={(id) =>
                    navigate(
                      workbenchHref(
                        id
                          ? { kind: 'fund', fund_id: id, client_id: scope.client_id }
                          : scope.client_id
                            ? { kind: 'client', client_id: scope.client_id }
                            : { kind: 'all' },
                        'other'
                      )
                    )
                  }
                />
              </label>
              <label>
                Type
                <select value={type} onChange={(e) => filter('other_type', e.target.value)}>
                  <option value="">All types</option>
                  {types.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </label>
              <label>
                Sort
                <select
                  value={oldest ? 'updated' : ''}
                  onChange={(e) => filter('other_sort', e.target.value)}
                >
                  <option value="">Urgent first</option>
                  <option value="updated">Updated newest first</option>
                </select>
              </label>
            </div>
            {!other ? (
              <p role="status">Other work could not be identified. Refresh before continuing.</p>
            ) : (
              <>
                <p className="other-caption">
                  {rows.length} {type ? 'matching ' : ''}items currently identified
                  {!other.complete ? ' · List may be incomplete' : ''}
                </p>
                <div className="fa-card other-table-wrap">
                  <table className="other-table">
                    <thead>
                      <tr>
                        <th>Action / subject</th>
                        <th>Type</th>
                        <th>Updated</th>
                        <th>Review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item) => (
                        <tr key={item.id} data-other-id={item.id}>
                          <td>
                            <strong>{item.label}</strong>
                            {item.subject ? <small>{item.subject}</small> : null}
                          </td>
                          <td>
                            {otherType(item)}
                            {item.priority === 'urgent' ? <small>Urgent</small> : null}
                          </td>
                          <td>
                            {item.updated_at
                              ? formatOfficerDateTime(item.updated_at)
                              : 'Not available'}
                          </td>
                          <td>
                            <Link to={otherHref(scope, item)}>Open review ›</Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!rows.length ? (
                  <p role="status">
                    {type
                      ? 'No matching Other items.'
                      : other.complete
                        ? 'No Other items currently identified.'
                        : 'No Other items loaded; coverage is incomplete.'}
                  </p>
                ) : null}
              </>
            )}
            {params.get('item') && !selected ? (
              <p role="status">The selected item is no longer in this Other queue.</p>
            ) : null}
            {selected?.kind === 'task' ? (
              <section className="fa-card other-task-review" aria-label="Selected Other review">
                <h2>{selected.label}</h2>
                <OtherTaskReview
                  key={selected.id + ':' + refresh}
                  item={selected}
                  scope={scope}
                  onResolved={reload}
                />
              </section>
            ) : null}
          </>
        ) : null}
        <ContextRecovery
          scope={scope}
          work={work}
          refresh={refresh}
          onChanged={reload}
          excludedTaskIds={other?.items.flatMap((item) => (item.task_id ? [item.task_id] : []))}
        />
      </div>
    </main>
  )
}
